/**
 * Typed access to the Cairn API.
 *
 * Errors carry the server's machine-readable `code`, never its English message,
 * so the UI can show a translated sentence instead of leaking developer prose
 * into a Chinese screen.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: {
        Accept: 'application/json',
        // Only when there is actually a body to describe. Announcing JSON and
        // then sending nothing — which every bodyless DELETE did — makes
        // Fastify reject the request with "Body cannot be empty when
        // content-type is set to 'application/json'", so nothing in the app
        // could be deleted.
        ...(init.body === undefined || init.body === null || init.body instanceof FormData
          ? {}
          : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'offline', 'Network request failed');
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const error = (body as { error?: { code: string; message: string; details?: unknown } } | null)
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'generic',
      error?.message ?? response.statusText,
      error?.details,
    );
  }

  return body as T;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});

// --- Shared shapes ----------------------------------------------------------

export type Role = 'admin' | 'member';
export type Theme = 'system' | 'light' | 'dark';
export type ExpenseStatus = 'open' | 'partial' | 'reimbursed' | 'ineligible';
export type PaymentMethod = 'out_of_pocket' | 'hsa_card' | 'other';
export type Eligibility = 'eligible' | 'ineligible' | 'unsure';
export type DocumentKind =
  'receipt' | 'eob' | 'invoice' | 'prescription' | 'statement' | 'tax_form' | 'other';

export type User = {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'invited' | 'disabled';
  locale: string;
  theme: Theme;
  avatarColor: string;
  personId: string | null;
  createdAt: string;
  lastLoginAt: string | null;
};

export type SessionResponse = {
  user: User | null;
  setupRequired: boolean;
  workspace: {
    name: string;
    currency: string;
    defaultLocale: 'en' | 'zh-CN';
  };
  capabilities: { email: boolean; paperless: boolean; sheets: boolean };
};

export type Access = 'view' | 'contribute' | 'manage';

/** A ledger is one HSA: its own receipts, balance, and list of who can see it. */
export type Ledger = {
  id: string;
  name: string;
  description: string | null;
  color: string;
  currency: string;
  hsaEstablishedDate: string | null;
  createdAt: string;
  receiptCount: number;
  peopleCount: number;
  access: Access;
};

export type Grant = {
  id: string;
  access: Access;
  userId: string | null;
  groupId: string | null;
  name: string;
  detail: string | null;
  avatarColor: string | null;
  memberCount: number | null;
};

export type Group = {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
};

/** Someone a receipt is *for*, within a ledger. Not a login. */
export type Person = {
  id: string;
  name: string;
  relationship: string | null;
  color: string;
  isSelf: boolean;
  sortOrder: number;
  archivedAt: string | null;
  receiptCount: number;
};

export type Category = {
  id: string;
  key: string;
  nameEn: string;
  nameZh: string;
  icon: string;
  color: string;
  sortOrder: number;
  isSystem: boolean;
  archivedAt: string | null;
};

export type Expense = {
  id: string;
  ledgerId: string;
  ledgerName: string | null;
  ledgerColor: string | null;
  personId: string | null;
  personName: string | null;
  personColor: string | null;
  categoryId: string | null;
  categoryKey: string | null;
  categoryNameEn: string | null;
  categoryNameZh: string | null;
  categoryIcon: string | null;
  categoryColor: string | null;
  provider: string | null;
  description: string | null;
  amountCents: number;
  currency: string;
  serviceDate: string;
  paidDate: string | null;
  paymentMethod: PaymentMethod;
  eligibility: Eligibility;
  reimbursedCents: number;
  reimbursedDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  status: ExpenseStatus;
  outstandingCents: number;
  documentCount: number;
  pendingDocumentCount: number;
  previewDocumentId: number | null;
};

/**
 * What Paperless read off a document, waiting on a person to confirm it.
 *
 * `source` says where each value came from, so the UI can be honest about how
 * much to trust it: a custom field somebody filled in is not the same kind of
 * claim as a number found next to the word "total".
 */
export type DocumentSuggestion = {
  amountCents: number | null;
  serviceDate: string | null;
  provider: string | null;
  source: {
    amount: 'label' | 'total' | 'custom-field' | null;
    serviceDate: 'text' | 'custom-field' | 'created' | null;
    provider: 'correspondent' | 'text' | null;
  };
};

export type ExpenseDocument = {
  id: string;
  expenseId: string;
  paperlessDocumentId: number | null;
  status: 'pending' | 'ready' | 'failed';
  suggestionState: 'none' | 'ready' | 'applied' | 'dismissed';
  suggestion: DocumentSuggestion | null;
  kind: DocumentKind;
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  error: string | null;
  createdAt: string;
};

export type ExpenseDetail = Expense & { documents: ExpenseDocument[] };

export type ExpenseListResponse = {
  expenses: Expense[];
  page: number;
  pageSize: number;
  total: number;
  totals: {
    count: number;
    totalCents: number;
    reimbursedCents: number;
    outstandingCents: number;
  };
};

export type Summary = {
  currency: string;
  hsaEstablishedDate: string | null;
  headline: {
    count: number;
    spentCents: number;
    reimbursedCents: number;
    availableCents: number;
    outOfPocketCents: number;
    missingDocuments: number;
    strandedCents: number;
    oldestClaimableDate: string | null;
  };
  byCategory: {
    categoryId: string | null;
    key: string | null;
    nameEn: string | null;
    nameZh: string | null;
    color: string | null;
    icon: string | null;
    count: number;
    spentCents: number;
    availableCents: number;
  }[];
  byPerson: {
    personId: string | null;
    name: string | null;
    color: string | null;
    count: number;
    spentCents: number;
    availableCents: number;
  }[];
  byMonth: {
    month: string;
    count: number;
    spentCents: number;
    reimbursedCents: number;
    availableCents: number;
  }[];
  byYear: {
    year: string;
    count: number;
    spentCents: number;
    reimbursedCents: number;
    availableCents: number;
  }[];
};

export type ExpenseFilters = {
  ledgerId?: string[];
  q?: string;
  from?: string;
  to?: string;
  personId?: string[];
  categoryId?: string[];
  status?: ExpenseStatus[];
  paymentMethod?: PaymentMethod[];
  minCents?: number;
  maxCents?: number;
  hasDocument?: boolean;
  sort?: string;
  page?: number;
  pageSize?: number;
};

export function filtersToQuery(filters: ExpenseFilters): string {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  };

  set('ledgerId', filters.ledgerId?.length ? filters.ledgerId : undefined);
  set('q', filters.q);
  set('from', filters.from);
  set('to', filters.to);
  set('personId', filters.personId?.length ? filters.personId : undefined);
  set('categoryId', filters.categoryId?.length ? filters.categoryId : undefined);
  set('status', filters.status?.length ? filters.status : undefined);
  set('paymentMethod', filters.paymentMethod?.length ? filters.paymentMethod : undefined);
  set('minCents', filters.minCents);
  set('maxCents', filters.maxCents);
  set('hasDocument', filters.hasDocument);
  set('sort', filters.sort);
  set('page', filters.page);
  set('pageSize', filters.pageSize);

  const query = params.toString();
  return query ? `?${query}` : '';
}

export type SettingsResponse = {
  workspaceName: string;
  currency: string;
  defaultLocale: 'en' | 'zh-CN';
  publicUrl: string;
  paperless: {
    url: string;
    token: { configured: boolean; hint: string | null };
    suggestions: boolean;
  };
  sheets: {
    enabled: boolean;
    spreadsheetId: string;
    serviceAccount: { configured: boolean; email: string | null };
    frequency: SheetsFrequency;
    lastSyncedAt: string | null;
    lastError: string | null;
  } | null;
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    from: string;
    password: { configured: boolean; hint: string | null };
  } | null;
  capabilities: { email: boolean; paperless: boolean; sheets: boolean };
};

/** How often the Google Sheets backup is rewritten. */
export type SheetsFrequency = 'entry' | 'hourly' | 'daily' | 'weekly' | 'manual';

export type SheetsSyncResult = {
  status: 'ok' | 'skipped' | 'failed';
  ledgers: number;
  rows: number;
  reason?: string;
};

export type UsersResponse = {
  users: User[];
  invitations: {
    id: string;
    email: string;
    name: string;
    role: Role;
    createdAt: string;
    expiresAt: string;
  }[];
  emailConfigured: boolean;
};

export type InviteResult = {
  id: string;
  link: string;
  expiresAt: string;
  emailed: boolean;
  emailError?: string | null;
};

// --- Endpoints --------------------------------------------------------------

export const api = {
  session: () => request<SessionResponse>('/auth/session'),
  setup: (body: {
    email: string;
    name: string;
    password: string;
    workspaceName?: string;
    ledgerName?: string;
    currency?: string;
    locale?: string;
  }) => request<{ ok: true }>('/auth/setup', json('POST', body)),
  login: (body: { email: string; password: string }) =>
    request<{ ok: true }>('/auth/login', json('POST', body)),
  logout: () => request<{ ok: true }>('/auth/logout', json('POST')),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    request<{ ok: true }>('/auth/change-password', json('POST', body)),
  forgotPassword: (body: { email: string }) =>
    request<{ ok: true; emailConfigured: boolean }>('/auth/forgot-password', json('POST', body)),
  checkReset: (token: string) => request<{ valid: boolean }>(`/auth/reset/${token}`),
  resetPassword: (token: string, password: string) =>
    request<{ ok: true }>(`/auth/reset/${token}`, json('POST', { password })),
  invitation: (token: string) =>
    request<{ email: string; name: string; workspaceName: string; defaultLocale: 'en' | 'zh-CN' }>(
      `/auth/invitation/${token}`,
    ),
  acceptInvitation: (token: string, body: { password: string; name?: string; locale?: string }) =>
    request<{ ok: true }>(`/auth/invitation/${token}/accept`, json('POST', body)),
  sessions: () =>
    request<{
      sessions: {
        id: string;
        createdAt: string;
        lastSeenAt: string;
        expiresAt: string;
        userAgent: string | null;
        ip: string | null;
      }[];
    }>('/auth/sessions'),
  revokeOtherSessions: () => request<{ ok: true }>('/auth/sessions/revoke-others', json('POST')),

  me: () => request<{ user: User }>('/me'),
  updateMe: (body: Partial<Pick<User, 'name' | 'locale' | 'theme' | 'avatarColor'>>) =>
    request<{ user: User }>('/me', json('PATCH', body)),

  ledgers: () => request<{ ledgers: Ledger[] }>('/ledgers'),
  ledger: (id: string) => request<{ ledger: Ledger; grants: Grant[] }>(`/ledgers/${id}`),
  createLedger: (body: {
    name: string;
    description?: string | null;
    color?: string;
    currency?: string;
    hsaEstablishedDate?: string | null;
  }) => request<{ id: string }>('/ledgers', json('POST', body)),
  updateLedger: (id: string, body: Record<string, unknown>) =>
    request<{ ok: true }>(`/ledgers/${id}`, json('PATCH', body)),
  archiveLedger: (id: string) => request<void>(`/ledgers/${id}`, { method: 'DELETE' }),

  grants: (ledgerId: string) => request<{ grants: Grant[] }>(`/ledgers/${ledgerId}/grants`),
  share: (ledgerId: string, body: { userId?: string; groupId?: string; access: Access }) =>
    request<{ id: string; updated: boolean }>(`/ledgers/${ledgerId}/grants`, json('POST', body)),
  changeAccess: (ledgerId: string, grantId: string, access: Access) =>
    request<{ ok: true }>(`/ledgers/${ledgerId}/grants/${grantId}`, json('PATCH', { access })),
  revokeAccess: (ledgerId: string, grantId: string) =>
    request<void>(`/ledgers/${ledgerId}/grants/${grantId}`, { method: 'DELETE' }),
  shareable: (ledgerId: string) =>
    request<{
      users: { id: string; name: string; email: string; avatarColor: string }[];
      groups: { id: string; name: string; memberCount: number }[];
    }>(`/ledgers/${ledgerId}/shareable`),

  people: (ledgerId: string) => request<{ people: Person[] }>(`/ledgers/${ledgerId}/people`),
  createPerson: (ledgerId: string, body: { name: string; relationship?: string; color?: string }) =>
    request<{ id: string }>(`/ledgers/${ledgerId}/people`, json('POST', body)),
  updatePerson: (ledgerId: string, personId: string, body: Record<string, unknown>) =>
    request<{ ok: true }>(`/ledgers/${ledgerId}/people/${personId}`, json('PATCH', body)),
  deletePerson: (ledgerId: string, personId: string) =>
    request<void>(`/ledgers/${ledgerId}/people/${personId}`, { method: 'DELETE' }),

  groups: () => request<{ groups: Group[] }>('/groups'),
  group: (id: string) =>
    request<{
      group: Group;
      members: { id: string; name: string; email: string; avatarColor: string }[];
    }>(`/groups/${id}`),
  createGroup: (body: { name: string; description?: string }) =>
    request<{ id: string }>('/groups', json('POST', body)),
  updateGroup: (id: string, body: Record<string, unknown>) =>
    request<{ ok: true }>(`/groups/${id}`, json('PATCH', body)),
  deleteGroup: (id: string) => request<void>(`/groups/${id}`, { method: 'DELETE' }),
  addGroupMember: (id: string, userId: string) =>
    request<{ ok: true }>(`/groups/${id}/members`, json('POST', { userId })),
  removeGroupMember: (id: string, userId: string) =>
    request<void>(`/groups/${id}/members/${userId}`, { method: 'DELETE' }),

  categories: () => request<{ categories: Category[] }>('/categories'),
  createCategory: (body: { nameEn: string; nameZh: string; icon?: string; color?: string }) =>
    request<{ id: string }>('/categories', json('POST', body)),
  updateCategory: (id: string, body: Record<string, unknown>) =>
    request<{ ok: true }>(`/categories/${id}`, json('PATCH', body)),

  expenses: (filters: ExpenseFilters) =>
    request<ExpenseListResponse>(`/expenses${filtersToQuery(filters)}`),
  expense: (id: string) => request<{ expense: ExpenseDetail; access: Access }>(`/expenses/${id}`),
  createExpense: (body: Record<string, unknown>) =>
    request<{ expense: ExpenseDetail }>('/expenses', json('POST', body)),
  updateExpense: (id: string, body: Record<string, unknown>) =>
    request<{ expense: ExpenseDetail }>(`/expenses/${id}`, json('PATCH', body)),
  reimburse: (id: string, body: { amountCents?: number; date?: string; note?: string }) =>
    request<{ expense: ExpenseDetail }>(`/expenses/${id}/reimburse`, json('POST', body)),
  unreimburse: (id: string) =>
    request<{ expense: ExpenseDetail }>(`/expenses/${id}/unreimburse`, json('POST')),
  deleteExpense: (id: string, purgeDocuments = false) =>
    request<void>(`/expenses/${id}?purgeDocuments=${purgeDocuments}`, { method: 'DELETE' }),

  uploadDocument: (expenseId: string, file: File, kind: DocumentKind = 'receipt') => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file, file.name);
    return request<{ documents: { id: string }[]; expense: ExpenseDetail }>(
      `/expenses/${expenseId}/documents`,
      { method: 'POST', body: form },
    );
  },
  decideSuggestion: (
    expenseId: string,
    documentId: string,
    body: {
      decision: 'apply' | 'dismiss';
      amountCents?: number;
      serviceDate?: string;
      provider?: string | null;
    },
  ) =>
    request<{ expense: ExpenseDetail }>(
      `/expenses/${expenseId}/documents/${documentId}/suggestion`,
      json('POST', body),
    ),

  deleteDocument: (expenseId: string, documentId: string, purge = false) =>
    request<void>(`/expenses/${expenseId}/documents/${documentId}?purge=${purge}`, {
      method: 'DELETE',
    }),

  summary: (filters: ExpenseFilters) =>
    request<Summary>(`/stats/summary${filtersToQuery(filters)}`),

  settings: () => request<SettingsResponse>('/settings'),
  updateSettings: (body: Record<string, unknown>) =>
    request<{ ok: true }>('/settings', json('PATCH', body)),
  connectPaperless: (body: { url: string; username: string; password: string }) =>
    request<{ ok: true; version: string | null; documentCount: number }>(
      '/settings/paperless/connect',
      json('POST', body),
    ),
  testPaperless: (body: { url?: string; token?: string }) =>
    request<{ ok: true; version: string | null; documentCount: number }>(
      '/settings/paperless/test',
      json('POST', body),
    ),
  testSmtp: () => request<{ ok: true }>('/settings/smtp/test', json('POST')),
  testSheets: (body: { spreadsheetId?: string; serviceAccount?: string }) =>
    request<{ ok: true; title: string; tabs: string[] }>(
      '/settings/sheets/test',
      json('POST', body),
    ),
  syncSheets: () => request<SheetsSyncResult>('/settings/sheets/sync', json('POST')),

  users: () => request<UsersResponse>('/users'),
  invite: (body: {
    email: string;
    name: string;
    role: Role;
    sendEmail: boolean;
    ledgerAccess?: { ledgerId: string; access: Access }[];
  }) => request<InviteResult>('/users/invitations', json('POST', body)),
  revokeInvitation: (id: string) => request<void>(`/users/invitations/${id}`, { method: 'DELETE' }),
  resendInvitation: (id: string) =>
    request<InviteResult>(`/users/${id}/resend-invite`, json('POST')),
  updateUser: (id: string, body: Record<string, unknown>) =>
    request<{ ok: true }>(`/users/${id}`, json('PATCH', body)),
  deleteUser: (id: string) => request<void>(`/users/${id}`, { method: 'DELETE' }),
  userResetLink: (id: string) =>
    request<{ link: string; expiresAt: string; emailed: boolean }>(
      `/users/${id}/reset-link`,
      json('POST'),
    ),

  importPreview: (ledgerId: string, tag?: string) =>
    request<{
      total: number;
      alreadyLinked: number;
      candidates: {
        paperlessId: number;
        title: string;
        created: string | null;
        amountCents: number | null;
        serviceDate: string | null;
      }[];
    }>(
      `/import/paperless/preview?ledgerId=${encodeURIComponent(ledgerId)}${
        tag ? `&tag=${encodeURIComponent(tag)}` : ''
      }`,
    ),
  runImport: (body: {
    ledgerId: string;
    tag?: string;
    personId?: string | null;
    categoryId?: string | null;
  }) => request<{ imported: number; skipped: number }>('/import/paperless', json('POST', body)),
};

/** Document URLs are plain hrefs so the browser can stream and cache them. */
export const documentUrl = (paperlessId: number, variant: 'preview' | 'thumb' | 'download') =>
  `/api/documents/${paperlessId}/${variant}`;

export const exportUrl = (kind: 'csv' | 'archive', filters: ExpenseFilters) =>
  `/api/export/${kind}${filtersToQuery(filters)}`;
